import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { axiosGet, axiosPost } from "../apis/useAxios";
import { meetsPolicy, usePasswordPolicy } from "../helpers/passwordPolicy";
import PasswordRuleList from "./Shared/PasswordRuleList";
import AuthShell, { AuthField } from "./auth/AuthShell";

const requestIdOf = (search) => new URLSearchParams(search ?? "").get("requestId") ?? "";

/**
 * Reset password from the emailed link (/reset-password?requestId=...): the link is checked first (auth/reset-password/{id});
 * an expired or used link says so and offers a new one. The new password follows the airline's rules.
 */
export function ResetPasswordComponent({ location }) {
  const requestId = requestIdOf(location?.search);
  const policy = usePasswordPolicy();
  const [link, setLink] = useState({ checking: true, ok: false, userName: "", message: "" });
  const [form, setForm] = useState({ newPassword: "", reTypePassword: "" });
  const [show, setShow] = useState({ newPassword: false, reTypePassword: false });
  const [errors, setErrors] = useState({ newPassword: "", reTypePassword: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setLink({ checking: false, ok: false, userName: "", message: "This link is not complete. Please ask for a new one." });
      return;
    }
    axiosGet(`auth/reset-password/${encodeURIComponent(requestId)}`)
      .then((r) => setLink({ checking: false, ok: Boolean(r?.isSuccess), userName: r?.payload?.userName ?? "", message: r?.message ?? "" }))
      .catch((err) => setLink({ checking: false, ok: false, userName: "", message: err?.message || "Unable to check the link." }));
  }, [requestId]);

  const change = (e) => {
    const { name, value } = e.target; // read now: React 16 reuses the event object
    setForm((f) => ({ ...f, [name]: value }));
  };
  const toggle = (name) => (e) => {
    e.preventDefault();
    setShow((s) => ({ ...s, [name]: !s[name] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {
      newPassword: form.newPassword === "" ? "Please enter new password." : "",
      reTypePassword: form.reTypePassword === "" ? "Please enter re-type password." : form.newPassword !== form.reTypePassword ? "Password does not match." : "",
    };
    setErrors(next);
    if (next.newPassword || next.reTypePassword) return;
    setSaving(true);
    try {
      const data = await axiosPost("auth/reset-password", { RequestId: requestId, NewPassword: form.newPassword });
      await Swal.fire({
        icon: data?.isSuccess ? "success" : "error",
        title: data?.isSuccess ? "Success" : "Oops...",
        text: data?.message || "Unable to reset the password.",
        confirmButtonText: data?.isSuccess ? "Go to login" : "OK",
      });
      if (data?.isSuccess) {
        window.location.href = "/";
        return;
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Oops...", text: err?.message || "Unable to reset the password." });
    }
    setSaving(false);
  };


  const passwordField = (name, label) => (
    <AuthField id={name} label={label} icon="fas fa-lock" error={errors[name]}>
      <input
        type={show[name] ? "text" : "password"}
        className="form-control"
        id={name}
        name={name}
        placeholder={label}
        maxLength={policy.maxLength}
        autoComplete="new-password"
        value={form[name]}
        onChange={change}
      />
      <button type="button" className="au-eye" aria-label={show[name] ? "Hide password" : "Show password"} onClick={toggle(name)}>
        <i className={show[name] ? "fas fa-eye-slash" : "fas fa-eye"} aria-hidden="true" />
      </button>
    </AuthField>
  );

  return (
    <AuthShell title="Reset your password" hint={link.ok ? `User name: ${link.userName}` : ""}>
      {link.checking && <p className="text-center text-muted">Checking the link…</p>}
      {!link.checking && !link.ok && (
        <>
          <div className="alert alert-danger au-alert" role="alert">
            <i className="fas fa-exclamation-circle mr-2" aria-hidden="true" />
            {link.message}
          </div>
          <Link className="btn btn-primary btn-lg btn-block" to="/forgot-password">
            <i className="fas fa-paper-plane mr-2" aria-hidden="true" />
            Send a new link
          </Link>
        </>
      )}
      {link.ok && (
        <form method="post" onSubmit={submit} noValidate style={saving ? { pointerEvents: "none", opacity: 0.5 } : {}}>
          {passwordField("newPassword", "New Password")}
          {passwordField("reTypePassword", "Re-type New Password")}
          <div className="au-rules">
            <PasswordRuleList policy={policy} password={form.newPassword} />
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={!meetsPolicy(policy, form.newPassword)}>
            <i className="fas fa-key mr-2" aria-hidden="true" />
            Reset your password
          </button>
        </form>
      )}
      <div className="au-back">
        <Link to="/" className="au-link">
          <i className="fas fa-arrow-left mr-1" aria-hidden="true" /> Back to Login
        </Link>
      </div>
    </AuthShell>
  );
}

export default ResetPasswordComponent;
